import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { llmProvider, getFactoryConfig, getAssemblyLine as apiGetAssemblyLine } from '../lib/api';
import { AssemblyLine, AssemblyLinePhase, AssemblyLineStep, FactoryConfig, Attachment } from '../types';
import { I, Icon } from '../components/Shared';
import { Shell } from '../components/Shell';
import { AttachmentBar } from '../components/AttachmentBar';

export function Landing() {
  const navigate = useNavigate();
  const bootstrapFromIdea = useStore((s) => s.bootstrapFromIdea);
  const bootstrapping = useStore((s) => s.bootstrapping);
  const bootstrapError = useStore((s) => s.bootstrapError);
  const assemblyLines = useStore((s) => s.assemblyLines);
  const loadAssemblyLines = useStore((s) => s.loadAssemblyLines);
  const selectedAssemblyLineId = useStore((s) => s.selectedAssemblyLineId);
  const selectAssemblyLine = useStore((s) => s.selectAssemblyLine);

  const [focused, setFocused] = useState(false);
  const [cfg, setCfg] = useState<FactoryConfig | null>(null);
  const [mode, setMode] = useState<string>('checking...');
  const [idea, setIdea] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [activeAssemblyLine, setActiveAssemblyLine] = useState<AssemblyLine | null>(null);
  // Per-run phase/step selection (Advanced settings). Keyed by step.key.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());

  useEffect(() => {
    llmProvider()
      .then((h) => setMode(h.fixtures ? `fixtures · ${h.provider}` : `${h.provider} · ${h.model}`))
      .catch(() => setMode('orchestrator offline'));
    getFactoryConfig()
      .then((c) => setCfg(c))
      .catch(() => setCfg(null));
    // Skip server fetch if demo assemblyLines are already loaded (demo mode)
    if (!useStore.getState().apps.some((a) => !a.id)) void loadAssemblyLines();
  }, [loadAssemblyLines]);

  // Auto-select the first assemblyLine if none is selected
  useEffect(() => {
    if (!selectedAssemblyLineId && assemblyLines.length > 0) {
      selectAssemblyLine(assemblyLines[0].id);
    }
  }, [assemblyLines, selectedAssemblyLineId, selectAssemblyLine]);

  // Fetch full assemblyLine details when selection changes (for branding + the
  // Advanced-settings picker). Switching lines re-initializes the selection to
  // every step of the new line, since phases/steps differ per line.
  useEffect(() => {
    if (!selectedAssemblyLineId) {
      setActiveAssemblyLine(null);
      setSelectedSteps(new Set());
      return;
    }
    apiGetAssemblyLine(selectedAssemblyLineId)
      .then((b) => {
        setActiveAssemblyLine(b);
        setSelectedSteps(new Set(b.phases.flatMap((p) => p.steps.map((s) => s.key))));
      })
      .catch(() => {
        setActiveAssemblyLine(null);
        setSelectedSteps(new Set());
      });
  }, [selectedAssemblyLineId]);

  // Derive branding from active assemblyLine, falling back to global config
  const brandLabel = activeAssemblyLine?.brandLabel || cfg?.brandLabel || 'App Factory';
  const headline = activeAssemblyLine?.brandHeadline || cfg?.brandHeadline || 'Where ideas become apps';
  const subheadline =
    activeAssemblyLine?.brandSubheadline || cfg?.brandSubheadline || 'Describe the app you want to build, and AI will create it for you';
  // Advanced-settings selection derived state.
  const allStepKeys = React.useMemo(
    () => (activeAssemblyLine?.phases ?? []).flatMap((p) => p.steps.map((s) => s.key)),
    [activeAssemblyLine],
  );
  const totalSteps = allStepKeys.length;
  const selStepCount = allStepKeys.filter((k) => selectedSteps.has(k)).length;
  const noStepsSelected = totalSteps > 0 && selStepCount === 0;

  const submit = async (): Promise<void> => {
    if (!idea.trim() || noStepsSelected) return;
    // Only send the subset when the user actually narrowed the run; a full
    // selection (or none loaded) submits undefined to preserve default behavior.
    const selectedStepKeys =
      totalSteps > 0 && selStepCount < totalSteps ? allStepKeys.filter((k) => selectedSteps.has(k)) : undefined;
    await bootstrapFromIdea(
      idea.trim(),
      {},
      selectedAssemblyLineId ?? undefined,
      undefined,
      selectedStepKeys,
      attachments.length ? attachments : undefined,
    );
    const proj = useStore.getState().project;
    if (proj) navigate(`/pipeline/${proj.id}`);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const sampleIdeas = [
    'OKR tracker with weekly check-ins',
    'Sprint retrospective board',
    'Risk register with severity scoring',
    'Engineering on-call rotation',
  ];

  return (
    <Shell active="home">
      {/* hero */}
      <main style={{ padding: '24px 28px 40px', maxWidth: 1100, margin: '0 auto' }}>
        <h1 className="t-display" style={{ margin: '0 0 10px', maxWidth: 720 }}>
          {headline}
        </h1>
        <p className="muted" style={{ margin: '0 0 36px', fontSize: 15, maxWidth: 620 }}>
          {subheadline}
        </p>

        {/* assemblyLine picker */}
        {assemblyLines.length > 0 && (
          <section style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
              <span className="t-micro">01 / Assembly line</span>
              <span className="muted t-small">choose an Assembly Line</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(assemblyLines.length, 4)}, minmax(0, 1fr))`,
                gap: 10,
              }}
            >
              {assemblyLines.map((b) => {
                const active = selectedAssemblyLineId === b.id;
                return (
                  <button
                    key={b.id}
                    data-assembly-line-id={b.id}
                    onClick={() => selectAssemblyLine(b.id)}
                    className="card"
                    style={{
                      textAlign: 'left',
                      padding: 16,
                      background: active ? 'var(--surface)' : 'var(--surface-2)',
                      borderColor: active ? 'var(--ink)' : 'var(--line)',
                      boxShadow: active ? '0 0 0 1px var(--ink)' : 'none',
                      cursor: 'pointer',
                      transition: 'all .15s',
                      fontFamily: 'inherit',
                      color: 'inherit',
                      position: 'relative',
                      minWidth: 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                      {(() => {
                        const chipLabel = b.slug?.toUpperCase() || b.name.slice(0, 5).toUpperCase();
                        return (
                          <span
                            className={`chip ${active ? 'chip--accent' : ''}`}
                            title={chipLabel}
                            style={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', lineHeight: '20px' }}
                          >
                            {chipLabel}
                          </span>
                        );
                      })()}
                      <span className="t-micro muted" style={{ flexShrink: 0 }}>v{b.version}</span>
                    </div>
                    <div className="t-h3" title={b.name} style={{ marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                    <div className="t-small muted" style={{ marginBottom: 14, minHeight: 36 }}>
                      {b.description}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, font: '500 11px var(--font-mono)', color: 'var(--ink-muted)' }}>
                      <span>{b.phaseCount} phases</span>
                      <span style={{ width: 3, height: 3, borderRadius: 99, background: 'var(--ink-faint)' }} />
                      <span>{b.stepCount} steps</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* advanced settings — per-run phase/step selection, above the idea box */}
        {activeAssemblyLine && totalSteps > 0 && (
          <PhaseStepPicker
            key={activeAssemblyLine.id}
            phases={activeAssemblyLine.phases}
            selected={selectedSteps}
            onChange={setSelectedSteps}
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
          />
        )}

        {/* idea input */}
        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
            <span className="t-micro">02 / Specification</span>
            <span className="muted t-small">describe the app you want to build</span>
          </div>
          <div
            className="card"
            style={{
              padding: 0,
              overflow: 'hidden',
              borderColor: 'var(--line-strong)',
              boxShadow: 'var(--shadow-2)',
            }}
          >
            <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)', alignItems: 'center' }}>
              <span className="t-micro">SPEC.MD</span>
              <span className="muted t-small">·</span>
              <span className="muted t-small">{activeAssemblyLine?.name || brandLabel}</span>
              <span style={{ flex: 1 }} />
              <span className="t-micro muted">{idea.length} / 2000</span>
            </div>
            <textarea
              className="textarea"
              data-demo-idea
              placeholder={`e.g. A time-tracking app that uses worklogs and shows weekly heatmaps per project...`}
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={onKey}
              disabled={bootstrapping}
              rows={4}
              style={{
                border: 0,
                background: 'transparent',
                fontSize: 14.5,
                padding: '18px 18px 14px',
                resize: 'none',
                width: '100%',
              }}
            />
            <div style={{ padding: '0 14px 12px' }}>
              <AttachmentBar
                attachments={attachments}
                onChange={setAttachments}
                disabled={bootstrapping}
                // label="Add files"
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 12px 10px 14px',
                borderTop: '1px solid var(--line)',
                background: 'var(--bg-sunken)',
              }}
            >
              <span className="t-micro muted">
                {bootstrapError
                  ? <span style={{ color: 'var(--err)' }}>{bootstrapError}</span>
                  : !selectedAssemblyLineId
                    ? 'Select an Assembly Line above to get started'
                    : '\u23CE to start · \u21E7\u23CE for newline'}
              </span>
              <span style={{ flex: 1 }} />
              <span className="t-micro muted" style={{ marginRight: 8 }} title="LLM provider/model (configured via env)">
                MODEL · {mode.toUpperCase()}
              </span>
              <button
                className="btn btn--accent btn--sm"
                disabled={bootstrapping || !selectedAssemblyLineId || !idea.trim() || noStepsSelected}
                onClick={submit}
              >
                {bootstrapping ? 'Building...' : <>Start run {I.play}</>}
              </button>
            </div>
          </div>

          {/* sample chips */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="t-micro" style={{ marginRight: 4 }}>TRY</span>
            {sampleIdeas.map((s) => (
              <button
                key={s}
                onClick={() => setIdea(s)}
                className="chip"
                style={{ cursor: 'pointer', height: 24, fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 12 }}
              >
                {s}
              </button>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  );
}

/* ─── Advanced settings: per-run phase & step picker ──────────── */

type CbxState = 'full' | 'partial' | 'none';

function Cbx({ state, size = 18 }: { state: CbxState; size?: number }): React.ReactElement {
  const filled = state === 'full';
  const partial = state === 'partial';
  return (
    <span
      style={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: size >= 18 ? 5 : 4,
        border: `1.5px solid ${filled || partial ? 'var(--accent)' : 'var(--line-strong)'}`,
        background: filled ? 'var(--accent)' : partial ? 'var(--accent-soft)' : 'var(--surface)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all .13s ease',
      }}
    >
      {filled && <Icon d="M4 12.5l5 5L20 6.5" size={size - 6} stroke={2.8} style={{ color: '#fff' }} />}
      {partial && <span style={{ width: size * 0.46, height: 2, borderRadius: 2, background: 'var(--accent)' }} />}
    </span>
  );
}

/** Small lozenge marking a phase/step the assembly line pins as non-skippable. */
function RequiredBadge(): React.ReactElement {
  return (
    <span
      style={{
        flexShrink: 0,
        font: '600 9.5px var(--font-mono)',
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'var(--accent-ink)',
        background: 'var(--accent-soft)',
        border: '1px solid transparent',
        padding: '2px 6px',
        borderRadius: 4,
      }}
    >
      Required
    </span>
  );
}

function PhaseStepPicker({
  phases,
  selected,
  onChange,
  open,
  onOpenChange,
}: {
  phases: AssemblyLinePhase[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  // Steps start collapsed; the user expands a phase (or uses "Expand") to reveal them.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const allStepKeys = phases.flatMap((p) => p.steps.map((s) => s.key));
  const totalSteps = allStepKeys.length;
  const totalPhases = phases.length;
  const selStepCount = allStepKeys.filter((k) => selected.has(k)).length;
  const selPhaseCount = phases.filter((p) => p.steps.some((s) => selected.has(s.key))).length;

  // A step is locked (can't be skipped) when it's marked required, or its phase
  // is required. Locked steps stay selected and their checkboxes are disabled.
  const stepLocked = (p: AssemblyLinePhase, s: AssemblyLineStep): boolean => !!(s.required || p.required);
  const lockedStepKeys = phases.flatMap((p) => p.steps.filter((s) => stepLocked(p, s)).map((s) => s.key));
  const phaseLocked = (p: AssemblyLinePhase): boolean => !!p.required || p.steps.every((s) => stepLocked(p, s));

  const phaseState = (p: AssemblyLinePhase): CbxState => {
    const n = p.steps.filter((s) => selected.has(s.key)).length;
    return n === 0 ? 'none' : n === p.steps.length ? 'full' : 'partial';
  };

  const toggleStep = (p: AssemblyLinePhase, s: AssemblyLineStep): void => {
    if (stepLocked(p, s)) return; // required — can't skip
    const next = new Set(selected);
    if (next.has(s.key)) next.delete(s.key);
    else next.add(s.key);
    onChange(next);
  };
  const togglePhase = (p: AssemblyLinePhase): void => {
    if (phaseLocked(p)) return; // required phase — can't skip
    const next = new Set(selected);
    const on = p.steps.some((s) => next.has(s.key));
    // Turning a phase off keeps its required steps selected.
    p.steps.forEach((s) => (on ? (stepLocked(p, s) ? next.add(s.key) : next.delete(s.key)) : next.add(s.key)));
    onChange(next);
  };
  const toggleExpand = (key: string): void => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const noteWarn = selStepCount === 0;
  const noteText = noteWarn
    ? 'Select at least one step — a run needs something to do.'
    : selStepCount < totalSteps
      ? "Skipped steps won't run, but later steps may expect their outputs."
      : 'Everything included — this is the full assembly line.';

  const linkBtn: React.CSSProperties = {
    background: 'none',
    border: 0,
    cursor: 'pointer',
    font: '600 11px var(--font-mono)',
    letterSpacing: '.03em',
    textTransform: 'uppercase',
    color: 'var(--ink-muted)',
    padding: '4px 6px',
    borderRadius: 5,
  };

  return (
    <div className="card" style={{ marginBottom: 28, overflow: 'hidden' }}>
      {/* header / summary row */}
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          padding: '12px 14px',
          background: 'transparent',
          border: 0,
          color: 'inherit',
          fontFamily: 'inherit',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            color: 'var(--ink-muted)',
            transition: 'transform .18s ease',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        >
          {I.chevR}
        </span>
        <span style={{ fontWeight: 600, fontSize: 13.5 }}>Advanced settings</span>
        <span style={{ flex: 1 }} />
        <span
          className="t-num"
          style={{
            font: '600 11px var(--font-mono)',
            color: 'var(--accent-ink)',
            background: 'var(--accent-soft)',
            padding: '3px 8px',
            borderRadius: 5,
            whiteSpace: 'nowrap',
          }}
        >
          {selPhaseCount} of {totalPhases} phases · {selStepCount} of {totalSteps} steps
        </span>
      </button>

      {/* body */}
      {open && (
        <div style={{ padding: '4px 14px 16px', borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 2px', flexWrap: 'wrap' }}>
            <span className="t-small muted">
              Choose the phases and steps this run will move through. Unchecked items are skipped.
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" style={linkBtn} onClick={() => onChange(new Set(allStepKeys))}>
                All
              </button>
              <button type="button" style={linkBtn} onClick={() => onChange(new Set(lockedStepKeys))}>
                None
              </button>
              <span style={{ width: 1, height: 16, background: 'var(--line)', margin: '0 4px' }} />
              <button type="button" style={linkBtn} onClick={() => setExpanded(new Set(phases.map((p) => p.key)))}>
                Expand
              </button>
              <button type="button" style={linkBtn} onClick={() => setExpanded(new Set())}>
                Collapse
              </button>
            </span>
          </div>

          {/* phase list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {phases.map((p, i) => {
              const st = phaseState(p);
              const on = st !== 'none';
              const isOpen = expanded.has(p.key);
              const selN = p.steps.filter((s) => selected.has(s.key)).length;
              const pLocked = phaseLocked(p);
              return (
                <div
                  key={p.key}
                  className="card"
                  style={{
                    background: on ? 'var(--surface)' : 'var(--surface-2)',
                    borderColor: on ? 'var(--line-strong)' : 'var(--line)',
                    opacity: on ? 1 : 0.6,
                    overflow: 'hidden',
                    transition: 'border-color .13s ease, opacity .13s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px' }}>
                    <span
                      role="checkbox"
                      aria-checked={st === 'full'}
                      aria-disabled={pLocked}
                      tabIndex={pLocked ? -1 : 0}
                      onClick={() => togglePhase(p)}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          togglePhase(p);
                        }
                      }}
                      title={pLocked ? 'Required phase — always runs' : undefined}
                      style={{ display: 'inline-flex', cursor: pLocked ? 'not-allowed' : 'pointer' }}
                    >
                      <Cbx state={st} />
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleExpand(p.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        flex: 1,
                        minWidth: 0,
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        color: 'inherit',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ font: '600 11px var(--font-mono)', color: 'var(--ink-faint)', width: 20, flexShrink: 0 }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-.003em' }}>{p.label}</span>
                          {pLocked && <RequiredBadge />}
                        </div>
                        <div
                          className="muted"
                          style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {p.desc}
                        </div>
                      </span>
                    </button>
                    <span
                      className="t-num"
                      style={{
                        flexShrink: 0,
                        font: '500 11px var(--font-mono)',
                        color: 'var(--ink-muted)',
                        background: 'var(--bg-sunken)',
                        border: '1px solid var(--line)',
                        padding: '3px 8px',
                        borderRadius: 20,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selN}/{p.steps.length} steps
                    </span>
                    <button
                      type="button"
                      aria-label="Toggle steps"
                      onClick={() => toggleExpand(p.key)}
                      style={{
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--ink-faint)',
                        transition: 'transform .18s ease',
                        transform: isOpen ? 'rotate(90deg)' : 'none',
                      }}
                    >
                      {I.chevR}
                    </button>
                  </div>

                  {/* step sublist */}
                  {isOpen && (
                    <div style={{ position: 'relative', padding: '2px 12px 10px 34px' }}>
                      <span
                        style={{ position: 'absolute', left: 21, top: 0, bottom: 14, width: 1, background: 'var(--line)' }}
                      />
                      {p.steps.map((s, si) => {
                        const son = selected.has(s.key);
                        const locked = stepLocked(p, s);
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => toggleStep(p, s)}
                            disabled={locked}
                            title={locked ? 'Required step — always runs' : undefined}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              width: '100%',
                              padding: '7px 10px',
                              background: 'none',
                              border: 0,
                              borderRadius: 6,
                              cursor: locked ? 'not-allowed' : 'pointer',
                              fontFamily: 'inherit',
                              color: 'inherit',
                              textAlign: 'left',
                              opacity: son ? 1 : 0.5,
                            }}
                          >
                            <Cbx state={son ? 'full' : 'none'} size={16} />
                            <span
                              style={{ font: '500 10.5px var(--font-mono)', color: 'var(--ink-faint)', width: 26, flexShrink: 0 }}
                            >
                              {i + 1}.{si + 1}
                            </span>
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 12.5,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {s.label}
                            </span>
                            {locked && <RequiredBadge />}
                            <span style={{ flexShrink: 0, font: '500 10.5px var(--font-mono)', color: 'var(--ink-faint)' }}>
                              {s.skillRef}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* validation note */}
          <div
            style={{
              marginTop: 12,
              padding: '9px 12px',
              borderRadius: 7,
              background: 'var(--bg-sunken)',
              border: `1px dashed ${noteWarn ? 'var(--err)' : 'var(--line-strong)'}`,
              fontSize: 12,
              color: noteWarn ? 'var(--err)' : 'var(--ink-muted)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <Icon
              d={
                <g>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8h.01M11 12h1v4h1" />
                </g>
              }
              size={14}
              stroke={2}
              style={{ marginTop: 1, color: noteWarn ? 'var(--err)' : 'var(--ink-faint)' }}
            />
            <span>{noteText}</span>
          </div>
        </div>
      )}
    </div>
  );
}
